import React, { CSSProperties, ReactNode, useEffect } from 'react'
import { useState } from 'react'
import ButtonOLD from '../../atoms/ButtonOLD'
import InputRadioGroup from '../InputRadioGroup'
import Modal from '../Modal/Modal'
import Text from '../../atoms/Text'
import colors from '@lmc/common/constants'
// @ts-ignore
import { Conflict, Log } from '@lmc/common'
import dayjs from 'dayjs'
import { isBrowser, isTablet } from 'react-device-detect'

export type ResolveModalProps = {
  setIsModalVisible: (visible?: boolean) => void
  isModalVisible: boolean
  children?: ReactNode
  localisation?: {
    title: string
    resolveText: string
    skipText: string
    conflict: string
    left: string
    loggedBy: string
  }
  conflicts?: Conflict[]
  onConflictResolve?: (payload: any) => Promise<any>
  getConflicts?: () => void
  isMobileLayout?: boolean
  loadingSpinner?: ReactNode
}

const ResolveModal = ({
  isModalVisible,
  setIsModalVisible,
  conflicts,
  onConflictResolve,
  isMobileLayout,
  getConflicts,
  localisation,
  loadingSpinner,
}: ResolveModalProps) => {
  const [listItem, setListItem] = useState(0)

  const [idToResolve, setIdToResolve] = useState(undefined)

  const [numberOfConflicts, setNumberOfConflicts] = useState<number>(0)

  useEffect(() => {
    if (!numberOfConflicts) {
      setNumberOfConflicts(conflicts?.length || 0)
    }
  }, [conflicts])

  const formatTitle = (log: Log) =>
    `${localisation?.conflict || ' · logged by'} ${
      log?.loggedBy?.name?.first
    }${' '}${log?.loggedBy?.name?.last}, ${dayjs(log.timeLogged).format(
      'HH:mm'
    )} `

  const getPagination = (conflicts: string | any[] | undefined) =>
    `${numberOfConflicts} ${localisation?.conflict || 'conflict'}${
      conflicts?.length !== 1 ? 's ' : ' '
    }${localisation?.left || 'left'}`

  const labelWidthWeb = 570

  const labelWidthMobile = 250

  const isMobile = !isBrowser && !isTablet

  const renderLabel = (log: Log) => (
    <div style={styles.labelRow}>
      <img
        src={log?.category?.icon?.url}
        style={{ ...styles.icon, backgroundColor: log?.category?.color }}
        alt="Icon"
      />
      <div style={styles.labelColumn as CSSProperties}>
        <Text as="p2" style={{ color: colors.darkgrey }}>
          <strong style={{ fontWeight: 500, color: colors.black }}>
            {log.title}
          </strong>
          {formatTitle(log)}
        </Text>
        <div
          style={{
            whiteSpace: 'normal',
            maxInlineSize: isMobile ? labelWidthMobile : labelWidthWeb,
          }}
        >
          {log.description}
        </div>
      </div>
    </div>
  )

  // @ts-ignore
  // eslint-disable-next-line no-unsafe-optional-chaining
  const shouldCloseModal = conflicts?.length - 1 === listItem

  const handleResolve = async () => {
    const result = await onConflictResolve?.({
      type: 'taskLogs',
      toDelete: conflicts?.[listItem].logs
        .filter((item: { id: undefined }) => item.id !== idToResolve)
        .map((conflict: { id: any }) => conflict.id),
      toKeep: idToResolve,
    })
    if (result) {
      setIdToResolve(undefined)
      getConflicts?.()
      setNumberOfConflicts(numberOfConflicts - 1)
      if (shouldCloseModal) {
        handleCancel()
      }
    }
  }

  const handleCancel = () => {
    setListItem(0)
    getConflicts?.()
    setIsModalVisible()
  }

  const handlePagination = () => {
    if (shouldCloseModal) {
      handleCancel()
    } else {
      setListItem(listItem + 1)
    }
  }

  return (
    <Modal
      width700px
      closeButton
      smallPadding
      setIsModalVisible={setIsModalVisible}
      isModalVisible={isModalVisible}
      onCancel={handleCancel}
    >
      <div>
        <Text style={{ color: colors.darkgrey }}>
          {localisation?.title ||
            'Conflict: Select the version of the recording you want to keep'}
        </Text>
      </div>
      {conflicts?.length
        ? [conflicts[listItem]]?.map((conflict) => (
            <div style={styles.radioWrapper}>
              <InputRadioGroup
                isMobileLayout={isMobileLayout}
                displayRow
                noBorder
                layout="row"
                // @ts-ignore
                value={idToResolve}
                options={conflict?.logs.map((log: { id: any }) => ({
                  value: log.id,
                  label: '',
                  renderLabel: () => renderLabel(log),
                }))}
                localization={{ errorMessage: 'Please select an option!' }}
                onChange={(e) => setIdToResolve(e)}
              />
            </div>
          ))
        : loadingSpinner || 'Loading...'}
      <div style={styles.paginationWrapper}>
        <Text as="p2">{getPagination(conflicts)}</Text>
        <div style={styles.resolveWrapper as CSSProperties}>
          <ButtonOLD type="secondary" link onClick={handlePagination}>
            {localisation?.skipText || 'Skip'}
          </ButtonOLD>
          <ButtonOLD
            style={styles.resolveButton}
            type="primary"
            onClick={handleResolve}
            disabled={!idToResolve}
          >
            {localisation?.resolveText || 'Resolve'}
          </ButtonOLD>
        </div>
      </div>
    </Modal>
  )
}

const styles = {
  bottomRow: {
    borderRadius: '0px 0px 25px 25px',
    paddingTop: 10,
    marginLeft: 4,
    marginRight: 4,
  },
  labelRow: { display: 'flex', alignItems: 'center' },
  icon: {
    backgroundColor: '#6D92EB',
    borderRadius: '50%',
    width: 28,
    height: 'auto',
    padding: 3,
    marginRight: 7,
  },
  labelColumn: { display: 'flex', flexDirection: 'column' },
  radioWrapper: {
    display: 'flex',
    borderWidth: 5,
    justifyContent: 'space-between',
    alignItems: 'center',
    overflow: 'auto',
  },
  paginationWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: '1px solid',
    borderColor: colors.grey,
    paddingTop: 24,
    paddingBottom: 24,
  },
  resolveWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resolveButton: {
    marginLeft: 10,
  },
}

export default ResolveModal
